import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Chip,
  Button,
  Paper,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import colors from 'colors/color';

// Import blog images
import blog1Image from '../../assets/img/blog_1.png';
import blog2Image from '../../assets/img/blog_2.png';
import blog3Image from '../../assets/img/blog_3.png';
import blog4Image from '../../assets/img/blog_4.png';
import blog5Image from '../../assets/img/blog_5.png';
import blog6Image from '../../assets/img/blog_6.png';
import NewAppBar from 'components/Navbars/AppBar';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: colors.surface,
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.primary} 0%, #0085FF 100%)`,
  color: colors.white,
  padding: theme.spacing(6, 3),
  marginBottom: theme.spacing(4),
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0, 23, 231, 0.15)',
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 4),
  },
}));

const BlogCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '16px',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${colors.border}`,
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 12px 24px rgba(0, 23, 231, 0.15)`,
    borderColor: colors.primary,
  },
}));

const StyledCardMedia = styled(CardMedia)({
  height: 240,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  position: 'relative',
});

const CategoryChip = styled(Chip)(({ theme }) => ({
  backgroundColor: colors.primary,
  color: colors.white,
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '28px',
  '&:hover': {
    backgroundColor: '#0085FF',
  },
}));

const ReadMoreButton = styled(Button)(({ theme }) => ({
  marginTop: 'auto',
  backgroundColor: colors.primary,
  color: colors.white,
  padding: theme.spacing(1.5, 3),
  borderRadius: '8px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  '&:hover': {
    backgroundColor: '#0085FF',
    transform: 'scale(1.02)',
  },
}));

const MetaInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  color: colors.textSecondary,
  fontSize: '0.875rem',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const MetaItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

// Blog data
const blogArticles = [
  {
    id: 1,
    title: "Les 10 erreurs à éviter lors d'un entretien d'embauche",
    slug: 'erreurs-entretien-embauche',
    description: "Les 10 erreurs les plus fréquentes en entretien, et surtout comment les éviter avec des exemples concrets.",
    image: blog1Image,
    readTime: '6–8 min',
    audience: 'Candidats',
    keywords: ['entretien d\'embauche', 'erreurs entretien', 'préparation entretien', 'questions entretien'],
  },
  {
    id: 2,
    title: 'Comment rédiger un CV percutant (et lisible par les ATS)',
    slug: 'cv-percutant-ats',
    description: 'Un CV clair, orienté impact, compatible ATS : structure, sections, erreurs à éviter, exemples.',
    image: blog2Image,
    readTime: '8–10 min',
    audience: 'Candidats',
    keywords: ['CV ATS', 'CV percutant', 'modèle CV', 'résumé CV', 'mots-clés CV'],
  },
  {
    id: 3,
    title: 'Les meilleures questions à poser à un candidat en entretien',
    slug: 'questions-entretien-candidat',
    description: 'Questions efficaces pour évaluer compétences, méthode, comportement et fit. Scorecards incluses.',
    image: blog3Image,
    readTime: '7–9 min',
    audience: 'Recruteurs',
    keywords: ['questions entretien', 'évaluation candidat', 'scorecard', 'recrutement structuré'],
  },
  {
    id: 4,
    title: 'Optimiser son profil LinkedIn pour attirer les recruteurs',
    slug: 'optimiser-linkedin-recruteurs',
    description: 'Headline, About, expériences, mots-clés, preuves : la méthode pour rendre ton profil trouvable et crédible.',
    image: blog4Image,
    readTime: '7–9 min',
    audience: 'Candidats',
    keywords: ['LinkedIn recruteur', 'profil LinkedIn', 'headline LinkedIn', 'résumé LinkedIn'],
  },
  {
    id: 5,
    title: 'Les compétences clés à mettre en avant sur un CV',
    slug: 'competences-cv-preuves',
    description: 'Hard skills, soft skills, preuves, formulations : comment choisir et démontrer tes compétences.',
    image: blog5Image,
    readTime: '6–8 min',
    audience: 'Candidats',
    keywords: ['compétences CV', 'hard skills', 'soft skills', 'preuve compétences'],
  },
  {
    id: 6,
    title: 'Recrutement moderne en 2026 : process, data, IA… sans perdre l\'humain',
    slug: 'recrutement-moderne-2026',
    description: 'Un cadre concret pour recruter mieux : scorecards, sourcing, automatisation, IA, expérience candidat.',
    image: blog6Image,
    readTime: '8–10 min',
    audience: 'RH / Dirigeants',
    keywords: ['recrutement 2026', 'IA recrutement', 'process recrutement', 'scorecard', 'ATS'],
  },
];

const Blogs = () => {
  const navigate = useNavigate();

  const handleReadMore = (slug) => {
    navigate(`/blog/${slug}`);
  };

  return (
  <>
  <NewAppBar/>
    <PageContainer>
        
      <Container maxWidth="xl">
        {/* Header Section */}
        <HeaderSection elevation={0}>
          <Typography
            variant="h2"
            sx={{
                color:'white',
              fontFamily: 'Poppins',
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3rem' },
              marginBottom: 2,
              textAlign: 'center',
            }}
          >
            Blog & Ressources
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 400,
              fontSize: { xs: '1rem', md: '1.25rem' },
              textAlign: 'center',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.95,
            }}
          >
            Conseils pratiques, astuces et guides pour réussir vos recrutements et
            optimiser votre recherche d'emploi
          </Typography>
        </HeaderSection>

        {/* Blog Grid */}
        <Grid container spacing={4}>
          {blogArticles.map((article) => (
            <Grid item xs={12} sm={6} lg={4} key={article.id}>
              <BlogCard>
                <StyledCardMedia image={article.image} title={article.title} />
                
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 3,
                  }}
                >
                  {/* Category Badge */}
                  <Box sx={{ marginBottom: 2 }}>
                    <CategoryChip label={article.audience} size="small" />
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="h5"
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 700,
                      fontSize: '1.25rem',
                      color: colors.textPrimary,
                      marginBottom: 2,
                      lineHeight: 1.4,
                      minHeight: '70px',
                    }}
                  >
                    {article.title}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'Poppins',
                      color: colors.textSecondary,
                      marginBottom: 2,
                      lineHeight: 1.6,
                      flexGrow: 1,
                    }}
                  >
                    {article.description}
                  </Typography>

                  {/* Meta Information */}
                  <MetaInfo>
                    <MetaItem>
                      <AccessTimeIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="caption">{article.readTime}</Typography>
                    </MetaItem>
                    <MetaItem>
                      <PersonIcon sx={{ fontSize: '1rem' }} />
                      <Typography variant="caption">{article.audience}</Typography>
                    </MetaItem>
                  </MetaInfo>

                  {/* Read More Button */}
                  <ReadMoreButton
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleReadMore(article.slug)}
                    fullWidth
                  >
                    Lire l'article
                  </ReadMoreButton>
                </CardContent>
              </BlogCard>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action Section */}
        <Box
          sx={{
            marginTop: 8,
            padding: 4,
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${colors.surface} 0%, ${colors.white} 100%)`,
            border: `2px solid ${colors.border}`,
            textAlign: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontFamily: 'Poppins',
              fontWeight: 700,
              color: colors.textPrimary,
              marginBottom: 2,
            }}
          >
            Besoin d'aide pour votre recrutement ?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'Poppins',
              color: colors.textSecondary,
              marginBottom: 3,
              maxWidth: '600px',
              margin: '0 auto 24px',
            }}
          >
            Talenty Hire  vous accompagne dans la structuration de votre processus de
            recrutement avec des outils performants et une approche moderne.
          </Typography>
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.primary,
              color: colors.white,
              padding: '12px 32px',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#0085FF',
              },
            }}
          >
            Découvrir Talenty Hire 
          </Button>
        </Box>
      </Container>
    </PageContainer></>
  );
};

export default Blogs;
